import type { LegacyTableColumn, LegacyTableRow } from "@wayflyer/flyui-dataviz";
import type { IntlShape } from "react-intl";

import { Dimensions, getMetricOverrideFromMetricType, MetricTypes } from "../autogen/metrics";
import { formatValue } from "../strings/type-formatting";
import { getMetricTypeString, MetaColumn } from "../strings/metric-strings";

import type { DashboardOutput, DataPoint } from "./fetch-api";
import type { Align } from "./metrics";
import { columnForMetric, getSort } from "./metrics";
import type { Metric } from "./types";

export const getRowContent = (dataPoint: DataPoint, currencyCode: string) => {
  const row: LegacyTableRow = {};
  Object.keys(dataPoint).forEach((metricType) => {
    const metric: Metric = getMetricOverrideFromMetricType(metricType as MetricTypes, currencyCode) as Metric;
    if (metric) {
      row[getSort(metric.name as MetricTypes)] = dataPoint[metric.name];
      row[metric.name] = formatValue({ value: dataPoint[metric.name].toString(), metricType: metric });
    }
  });
  return row;
};

export const toTableRows = (output: DashboardOutput, aggregateBy: Dimensions): { data: LegacyTableRow[]; summary } => {
  const rv: LegacyTableRow[] = [];
  output.data.forEach((dataPoint) => {
    rv.push({ [aggregateBy]: dataPoint[aggregateBy], ...getRowContent(dataPoint, output.currencyCode) });
  });

  const summary = { [aggregateBy]: "Summary", ...getRowContent(output.summary, output.currencyCode) };

  return { data: rv, summary };
};

export type ColumnRename = {
  metricType: MetricTypes;
  title: string;
};

export const toColumns = (
  output: DashboardOutput,
  firstColumn: Dimensions | MetricTypes,
  renameCols: ColumnRename[],
  summaryAlign: Align = "left",
  columnsToInclude: MetricTypes[] | null = null,
  intl: IntlShape | null = null
): LegacyTableColumn[] => {
  const rv: LegacyTableColumn[] = [];

  rv.push({
    name: intl ? intl.formatMessage(getMetricTypeString(firstColumn)) : firstColumn,
    id: firstColumn,
    align: summaryAlign,
  });

  // TODO move to always doing it this way, i.e. passing in intl
  if (columnsToInclude) {
    columnsToInclude.forEach((metricType) => {
      rv.push({
        name: intl ? intl.formatMessage(getMetricTypeString(metricType)) : metricType,
        id: metricType,
        sortDescFirst: true,
        align: "right" as Align,
      });
    });
  } else {
    output.metrics.forEach((metric) => {
      const column = columnForMetric(metric);
      const rename = renameCols.findIndex((val) => metric.name === val.metricType);
      if (rename > -1) {
        column.name = renameCols[rename].title;
      }
      rv.push(column);
    });
  }
  return rv;
};

export const getToolTipForColumn = (intl) => (columnId: LegacyTableColumn["id"]) => {
  return intl.formatMessage(getMetricTypeString(columnId as MetricTypes | Dimensions | MetaColumn, "tooltip"));
};
